import { createElement, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBranchScheduleSetting } from '../../../api/branchScheduleSetting';
import { getRoomId, isRoomAllowedForLesson } from '../../../constants/roomTypes';
import { normalizeWeekStartDay } from '../weekStart';

/**
 * 지점 스케줄 설정 (설정 모달에서 저장, 일간·주간·월간 화면이 읽는다).
 * API 가 아직 없거나 실패해도 화면이 그대로 돌도록 항상 기본값으로 채워서 쓴다.
 * 기본값은 설정 도입 전 화면과 같은 동작이다(1시간 칸·1시간 선택, 카드 최소 48분, 50분 수업, 칩 전부 표시).
 */
// 표 칸 단위(분) — 왼쪽 시간 줄·그리드 행 하나의 길이
export const TIME_UNIT_OPTIONS = [30, 60, 120];
// 선택 단위(분) — 빈칸 hover·클릭 생성·우클릭 붙여넣기·드래그/드롭 스냅 간격. 표 칸과 따로 정한다.
export const SNAP_OPTIONS = [5, 10, 15, 20, 30, 60];
export const CARD_MIN_OPTIONS = [15, 20, 30, 45, 48, 60];
// 카드 최소 높이 기본값 — 기존 '1시간의 80%'와 같은 48분
export const DEFAULT_CARD_MIN_MINUTES = 48;

// 기본 수업 시간을 두는 구분 (설정 키 → 라벨)
export const DURATION_TYPES = [
  { key: 'PRIVATE_LESSON', label: '개인레슨' },
  { key: 'DUET_LESSON', label: '듀엣레슨' },
  { key: 'OT', label: 'OT' },
  { key: 'CONSULTATION', label: '상담' },
  { key: 'ETC', label: '기타' },
];

// 기본 강의실을 두는 구분
export const ROOM_DEFAULT_TYPES = DURATION_TYPES.slice(0, 3);

// 요약 카운트 칩 (설정 키 → 라벨)
export const COUNT_CHIPS = [
  { key: 'SCHEDULE', label: '스케줄' },
  { key: 'RESERVED', label: '예약' },
  { key: 'ATTENDED', label: '출석' },
  { key: 'ABSENT', label: '결석' },
  { key: 'GROUP', label: '그룹' },
  { key: 'OT', label: 'OT' },
  { key: 'CONSULT', label: '상담' },
  { key: 'ETC', label: '기타' },
];

// 유형 항목 — 끄면 그 유형 일정을 지점 스케줄에서 통째로 숨긴다(카드·패널·유형 드롭다운·칩·강사별 숫자).
// 설정 키 → 일정 courseClassType. 개인·듀엣은 항목이 없어 항상 보인다.
export const TYPE_COUNT_KEY_TO_CLASS_TYPE = {
  GROUP: 'GROUP_LESSON',
  OT: 'OT',
  CONSULT: 'CONSULTATION',
  ETC: 'ETC',
};

// 상태 항목 — 끄면 숫자(상단 칩·일간 강사별 숫자)만 숨긴다. 카드는 그대로.
export const STATUS_COUNT_KEYS = ['SCHEDULE', 'RESERVED', 'ATTENDED', 'ABSENT'];

/** 표시를 끈 유형의 courseClassType 목록 */
export const getHiddenClassTypes = visibleCounts =>
  Object.entries(TYPE_COUNT_KEY_TO_CLASS_TYPE)
    .filter(([key]) => !visibleCounts.includes(key))
    .map(([, classType]) => classType);

// 숨긴 유형 일정과 충돌했을 때 붙이는 안내
export const HIDDEN_TYPE_CONFLICT_NOTE = '숨긴 유형의 일정입니다 — 설정에서 표시를 켜면 볼 수 있습니다';

/** 줄바꿈(\n)이 든 문구를 토스트 하나에 여러 줄로 보여준다. 토스트는 문자열을 그대로 그려 줄바꿈을 살리려 span 으로 감싼다. */
export const toMultilineToast = message => createElement('span', { style: { whiteSpace: 'pre-line' } }, message);

/** 에러 문구 + 줄바꿈 + (숨긴 유형 안내) 를 토스트 하나에 담는다. */
export const withHiddenTypeNote = message => toMultilineToast(`${message}\n(${HIDDEN_TYPE_CONFLICT_NOTE})`);

/** 충돌(409) 응답의 일정 유형이 숨긴 유형인지. conflict = { type: CLASS|CONSULTATION, classType? } */
export const isHiddenConflict = (conflict, hiddenClassTypes) => {
  if (!conflict || hiddenClassTypes.length === 0) return false;
  const classType = conflict.type === 'CONSULTATION' ? 'CONSULTATION' : conflict.classType;
  return !!classType && hiddenClassTypes.includes(classType);
};

// 헤더 칩 필터 값(attendFilters)·유형 드롭다운 값(typeFilter) → 설정 키
export const FILTER_VALUE_TO_COUNT_KEY = {
  reserved: 'RESERVED',
  attended: 'ATTENDED',
  absented: 'ABSENT',
  group: 'GROUP',
  ot: 'OT',
  consult: 'CONSULT',
  etc: 'ETC',
};

// 기본 수업 시간 입력 범위(분). 시간 선택이 5분 단위라 5분 배수만 받는다.
export const DURATION_MIN = 5;
export const DURATION_MAX = 600;
export const DURATION_STEP = 5;

const DEFAULT_DURATION = 50;

export const SCHEDULE_SETTING_DEFAULTS = {
  timeUnitMinutes: 60,
  snapMinutes: 60,
  cardMinMinutes: DEFAULT_CARD_MIN_MINUTES,
  defaultDurations: Object.fromEntries(DURATION_TYPES.map(({ key }) => [key, DEFAULT_DURATION])),
  defaultRoomIds: Object.fromEntries(ROOM_DEFAULT_TYPES.map(({ key }) => [key, null])),
  visibleCounts: COUNT_CHIPS.map(({ key }) => key),
  // 주 시작 요일 — 응답에 없으면 설정 도입 전 지점 스케줄 동작(일요일 시작)
  weekStartDay: 'SUNDAY',
};

export const isValidDuration = value =>
  Number.isInteger(value) && value >= DURATION_MIN && value <= DURATION_MAX && value % DURATION_STEP === 0;

/** 서버 응답(또는 undefined)을 화면이 믿고 쓸 수 있는 값으로 맞춘다 — 빠지거나 이상한 값은 기본값 */
export const normalizeScheduleSetting = data => {
  const defaults = SCHEDULE_SETTING_DEFAULTS;
  if (!data || typeof data !== 'object') return defaults;

  const timeUnitMinutes = TIME_UNIT_OPTIONS.includes(Number(data.timeUnitMinutes))
    ? Number(data.timeUnitMinutes)
    : defaults.timeUnitMinutes;
  // 선택 단위가 응답에 아예 없으면(선택 단위 도입 전 백엔드) 예전처럼 표 칸 단위를 따른다 — 30분 칸 지점의 클릭·드래그가
  // 30분 간격 그대로 유지된다. 표 칸이 선택지에 없는 값(2시간)이면 기본값.
  const snapMinutes = SNAP_OPTIONS.includes(Number(data.snapMinutes))
    ? Number(data.snapMinutes)
    : data.snapMinutes == null && SNAP_OPTIONS.includes(timeUnitMinutes)
      ? timeUnitMinutes
      : defaults.snapMinutes;
  const cardMinMinutes = CARD_MIN_OPTIONS.includes(Number(data.cardMinMinutes))
    ? Number(data.cardMinMinutes)
    : defaults.cardMinMinutes;

  const defaultDurations = Object.fromEntries(
    DURATION_TYPES.map(({ key }) => {
      const value = Number(data.defaultDurations?.[key]);
      return [key, isValidDuration(value) ? value : defaults.defaultDurations[key]];
    }),
  );

  const defaultRoomIds = Object.fromEntries(
    ROOM_DEFAULT_TYPES.map(({ key }) => {
      const value = data.defaultRoomIds?.[key];
      return [key, value == null || value === '' ? null : Number(value)];
    }),
  );

  const knownCountKeys = COUNT_CHIPS.map(({ key }) => key);
  const visibleCounts = Array.isArray(data.visibleCounts)
    ? data.visibleCounts.filter(key => knownCountKeys.includes(key))
    : defaults.visibleCounts;

  const weekStartDay = normalizeWeekStartDay(data.weekStartDay, defaults.weekStartDay);

  return { timeUnitMinutes, snapMinutes, cardMinMinutes, defaultDurations, defaultRoomIds, visibleCounts, weekStartDay };
};

export const branchScheduleSettingQueryKey = branchId => ['branchScheduleSetting', branchId];

/**
 * 지점 스케줄 설정 조회. 실패하면 화면은 조용히 기본값으로 돈다 — 설정은 보조 기능이라 화면을 막지 않는다.
 * 다만 설정 모달은 기본값을 저장하면 서버 설정을 덮어쓰므로 isLoading(아직 못 받음)·isError 를 함께 돌려준다.
 */
export const useBranchScheduleSetting = (branchId, token) => {
  const { data, isPending, isError } = useQuery({
    queryKey: branchScheduleSettingQueryKey(branchId),
    queryFn: () => getBranchScheduleSetting(branchId, token),
    enabled: !!branchId && !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const setting = useMemo(() => normalizeScheduleSetting(data), [data]);
  // 비활성(지점·토큰 없음)일 때도 isPending 이라 '아직 못 받음'으로 본다
  return { setting, isLoading: isPending && !isError, isError };
};

// 생성 모달의 레슨구분(lessonType) → 설정 키. 상담은 모달에서 COUNSEL 로 부른다.
const LESSON_TYPE_TO_SETTING_KEY = {
  PRIVATE_LESSON: 'PRIVATE_LESSON',
  DUET_LESSON: 'DUET_LESSON',
  OT: 'OT',
  COUNSEL: 'CONSULTATION',
  CONSULTATION: 'CONSULTATION',
  ETC: 'ETC',
};

// 시간 선택지는 0~23시, 5분 단위라 종료는 23:55 를 넘길 수 없다
const LAST_SELECTABLE_MINUTES = 23 * 60 + 55;

/**
 * 새 일정의 구분별 기본값 — 종료 시각(시작 + 기본 수업 시간)과 기본 강의실.
 * 기본 강의실이 없는 구분(상담·기타)은 exerciseRoomId 를 돌려주지 않는다(모달의 기존 값을 그대로 둔다).
 * 기본 강의실이 비어 있거나 지금 목록(rooms)에 없거나 유형이 안 맞으면 -1(미선택)을 돌려줘
 * 기존 자동 선택(센터설정 기본 강의실·하나뿐인 강의실)이 동작하게 한다.
 */
export const getCreateDefaults = (setting, lessonType, startHour, startMinute = 0, rooms = []) => {
  const key = LESSON_TYPE_TO_SETTING_KEY[lessonType] || 'PRIVATE_LESSON';
  const duration = setting.defaultDurations[key] ?? DEFAULT_DURATION;
  const startTotal = Number(startHour || 0) * 60 + Number(startMinute || 0);
  const endTotal = Math.min(startTotal + duration, LAST_SELECTABLE_MINUTES);
  // 23:55 에서 잘려 종료가 시작 이하가 되면 종료는 채우지 않는다(모달의 기존 값을 둔다)
  const result = endTotal > startTotal ? { endHour: Math.floor(endTotal / 60), endMinute: endTotal % 60 } : {};
  if (key in setting.defaultRoomIds) {
    const roomId = setting.defaultRoomIds[key];
    const room = roomId == null ? null : rooms.find(item => getRoomId(item) === roomId);
    result.exerciseRoomId = room && isRoomAllowedForLesson(room, key) ? roomId : -1;
  }
  return result;
};
